import { createClient } from '@supabase/supabase-js'
import { toast } from 'sonner'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './env'

// These will be set from environment variables or user settings
export const supabaseUrl = SUPABASE_URL
const supabaseAnonKey = SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl!, supabaseAnonKey!, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'brian2-auth', // Use consistent storage key to avoid multiple instances warning
  },
  global: {
    headers: {
      // Add custom headers if needed
    },
  },
})

// Expose supabase globally for debugging (dev only)
if (typeof window !== 'undefined') {
  ;(window as any).supabase = supabase
}

// ── Proactive session refresh on tab focus ────────────────────────
// When user returns to a backgrounded tab, autoRefreshToken may have
// missed its timer. Force a refresh to avoid 401 errors.
let lastVisibleAt = Date.now()

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return

    const awayMs = Date.now() - lastVisibleAt
    lastVisibleAt = Date.now()

    // Only refresh if tab was hidden for >2 minutes
    if (awayMs < 2 * 60 * 1000) return

    console.log(`[Supabase Auth] Tab regained focus after ${Math.floor(awayMs / 1000)}s — refreshing session`)
    const { error } = await supabase.auth.refreshSession()
    if (error) {
      console.error('[Supabase Auth] Session refresh failed:', error.message)
      toast.error('Sessionen har gått ut', {
        description: 'Ladda om sidan för att logga in igen.',
        action: { label: 'Ladda om', onClick: () => window.location.reload() },
        duration: Infinity,
      })
    }
  })
}

// ── Native fetch — saved before any patching ─────────────────────
// Import this in code that must bypass the global 401 interceptor
// (e.g., long-running imports that manage their own auth).
export const nativeFetch: typeof fetch = typeof window !== 'undefined'
  ? window.fetch.bind(window)
  : (globalThis.fetch || (() => { throw new Error('No fetch available') })) as typeof fetch

// ── 401 interceptor — show toast on auth failures ─────────────────
// Monkey-patch fetch to detect Supabase 401 responses and alert user.
if (typeof window !== 'undefined' && supabaseUrl) {
  let authToastShown = false

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args)

    if (response.status === 401 && !authToastShown) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url
      if (url?.includes(supabaseUrl)) {
        authToastShown = true
        console.error('[Supabase Auth] 401 detected — session expired')

        // Try one automatic refresh before showing toast
        const { error } = await supabase.auth.refreshSession()
        if (error) {
          toast.error('Sessionen har gått ut', {
            description: 'Ändringar kan inte sparas. Ladda om sidan.',
            action: { label: 'Ladda om', onClick: () => window.location.reload() },
            duration: Infinity,
          })
        } else {
          console.log('[Supabase Auth] Auto-recovered from 401 via refresh')
          authToastShown = false
        }
      }
    }

    return response
  }
}

// Listen for auth state changes and log token expiry
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('[Supabase Auth] Token refreshed successfully')
  } else if (event === 'SIGNED_OUT') {
    console.log('[Supabase Auth] User signed out')
  } else if (event === 'USER_UPDATED') {
    console.log('[Supabase Auth] User updated')
  }

  if (session?.expires_at) {
    const expiresIn = session.expires_at - Math.floor(Date.now() / 1000)
    console.log(`[Supabase Auth] Token expires in ${Math.floor(expiresIn / 60)} minutes`)
  }
})

export type Database = {
  public: {
    Tables: {
      tasks: {
        Row: {
          id: string
          user_id: string
          name: string
          raw_name: string | null
          note: string | null
          due_date: string | null
          due_time: string | null
          duration: number | null
          completed: boolean
          status: string | null
          priority: number
          tags: string[] | null
          repeat: any | null
          repeat_id: string | null
          project: string | null
          project_path: string | null
          source: string
          source_url: string | null
          external_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          name: string
          raw_name?: string | null
          note?: string | null
          due_date?: string | null
          due_time?: string | null
          duration?: number | null
          completed?: boolean
          status?: string | null
          priority?: number
          tags?: string[] | null
          repeat?: any | null
          repeat_id?: string | null
          project?: string | null
          project_path?: string | null
          source: string
          source_url?: string | null
          external_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          name?: string
          raw_name?: string | null
          note?: string | null
          due_date?: string | null
          due_time?: string | null
          duration?: number | null
          completed?: boolean
          status?: string | null
          priority?: number
          tags?: string[] | null
          repeat?: any | null
          repeat_id?: string | null
          project?: string | null
          project_path?: string | null
          source?: string
          source_url?: string | null
          external_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}
